import React, { useContext, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Paper,
  Rating,
  Stack,
  Typography,
} from "@mui/material";

import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import AppleIcon from "@mui/icons-material/Apple";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";

import { UserContext } from "../../context/userContext/userContext";
import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";
import { useStyle } from "../../context/styleContext/styleContext";
import LoginRegisterModal from "../LoginRegisterModal/LoginRegisterModal";

function getCardImageScale(productData) {
  const configuredScale = Number(productData?.cardImageScale);

  if (Number.isFinite(configuredScale) && configuredScale > 0) {
    const normalizedScale =
      configuredScale > 1 ? configuredScale / 100 : configuredScale;

    return Math.min(1, Math.max(0.5, normalizedScale));
  }

  return productData?.productURI === "d1-delay" ? 0.92 : 0.82;
}

export default function ProductsPageCard({ productData }) {
  const { user } = useContext(UserContext);
  const { addToCart } = useContext(ShoppingCartContext);
  const { getProductDisplayPricing } = usePromotions();
  const { style } = useStyle();

  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [downloadRequest, setDownloadRequest] = useState(null);
  const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);

  const productPricing = useMemo(() => {
    if (!productData || Number(productData.price || 0) <= 0) {
      return null;
    }

    return getProductDisplayPricing(productData);
  }, [productData, getProductDisplayPricing]);

  if (!productData) {
    return null;
  }

  const productUrl = `/products/${productData.productURI}`;
  const reviewUrl = `/reviews/${productData.productURI}`;

  const isFree = Number(productData.price || 0) <= 0;
  const rating = Number(productData.starRating || 0);
  const ratingCount = Number(productData.ratingCount || 0);

  const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
  const finalPrice = Number(
    productPricing?.finalPrice ?? productData.price ?? 0
  );
  const originalPrice = Number(
    productPricing?.originalPrice ?? productData.price ?? 0
  );
  const msrp = Number(productData.msrp || 0);

  const comparisonPrice = hasPromoDiscount
    ? originalPrice
    : msrp > finalPrice
      ? msrp
      : null;

  const savingsPercent =
    hasPromoDiscount && originalPrice > 0
      ? Math.round(
          (Number(productPricing?.discountAmount || 0) / originalPrice) * 100
        )
      : 0;

  const mainImage = productData.images?.find((image) => image?.URI);
  const imageScale = getCardImageScale(productData);
  const hasWindowsDownload = Boolean(productData.windowsDownloadURI);
  const hasMacDownload = Boolean(productData.macDownloadURI);
  const hasDownloadOption = hasWindowsDownload || hasMacDownload;

  const formatPrice = (price) => Number(price || 0).toFixed(2);

  const handleAddToCart = () => {
    addToCart(productData.SKU);
  };

  const openDownloadMenu = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const closeDownloadMenu = () => {
    setDownloadMenuAnchor(null);
  };

  const registerAndDownloadFreeProduct = async (platform) => {
    closeDownloadMenu();

    const token = localStorage.getItem("token");

    if (!user && !token) {
      setDownloadRequest(platform);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/register_free_product", {
        token,
        sku: productData.SKU,
        productVersion: productData.productVersion,
      });

      if (platform === "windows" && productData.windowsDownloadURI) {
        window.location.href = productData.windowsDownloadURI;
      }

      if (platform === "mac" && productData.macDownloadURI) {
        window.location.href = productData.macDownloadURI;
      }
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const loginCallback = async () => {
    const token = localStorage.getItem("token");

    if (!token || !downloadRequest) {
      setLoginRegisterModalOpen(false);
      setDownloadRequest(null);
      return;
    }

    try {
      await axios.post("/api/register_free_product", {
        token,
        sku: productData.SKU,
        productVersion: productData.productVersion,
      });

      const requestedPlatform = downloadRequest;

      setDownloadRequest(null);
      setLoginRegisterModalOpen(false);

      if (
        requestedPlatform === "windows" &&
        productData.windowsDownloadURI
      ) {
        window.location.href = productData.windowsDownloadURI;
        return;
      }

      if (requestedPlatform === "mac" && productData.macDownloadURI) {
        window.location.href = productData.macDownloadURI;
      }
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const closeLoginRegisterModal = () => {
    setLoginRegisterModalOpen(false);
    setDownloadRequest(null);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "15px",
          backgroundColor: style.color1,
          border: `1px solid ${style.color2}`,
          boxShadow:
            "0 8px 22px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.025)",
          boxSizing: "border-box",
          transition:
            "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
          "&:hover": {
            transform: { sm: "translateY(-3px)" },
            borderColor: "rgba(125,165,215,0.42)",
            boxShadow:
              "0 14px 30px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04)",
          },
          "&:hover .products-page-card-image": {
            transform: { sm: "scale(1.025)" },
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 1.1, sm: 1.2 },
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Stack
            direction={{ xs: "row", sm: "column" }}
            spacing={{ xs: 1.15, sm: 1 }}
            sx={{ minWidth: 0 }}
          >
            <Box
              component={RouterLink}
              to={productUrl}
              aria-label={`View ${productData.name}`}
              sx={{
                position: "relative",
                width: { xs: 132, sm: "100%" },
                height: { xs: 132, sm: 170 },
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                borderRadius: "11px",
                textDecoration: "none",
                backgroundColor: style.darkerPanel,
                border: "1px solid rgba(255,255,255,0.10)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.025)",
                p: { xs: 0.25, sm: 0.9 },
                boxSizing: "border-box",
              }}
            >
              {mainImage?.URI ? (
                <Box
                  className="products-page-card-image"
                  component="img"
                  src={mainImage.URI}
                  alt={mainImage.altText || productData.name}
                  sx={{
                    width: {
                      xs: `${Math.min(1, imageScale + 0.1) * 100}%`,
                      sm: `${imageScale * 100}%`,
                    },
                    maxWidth: "100%",
                    maxHeight: { xs: 124, sm: 148 },
                    objectFit: "contain",
                    display: "block",
                    transition: "transform 180ms ease",
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    px: 1,
                    color: style.color3,
                    fontSize: "0.82rem",
                    textAlign: "center",
                  }}
                >
                  Image unavailable
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                component={RouterLink}
                to={productUrl}
                sx={{
                  color: style.color4,
                  textDecoration: "none",
                  fontWeight: 800,
                  fontSize: { xs: "1rem", sm: "1.08rem" },
                  lineHeight: 1.18,
                  minHeight: { xs: "auto", sm: "2.36em" },
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  "&:hover": {
                    color: "#fff",
                  },
                }}
              >
                {productData.name}
              </Typography>

              <Box
                component={RouterLink}
                to={reviewUrl}
                sx={{
                  mt: 0.65,
                  minHeight: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "fit-content",
                  maxWidth: "100%",
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                {
                ratingCount == 1 ? (
                    <>
                      <Rating
                        value={rating}
                        precision={0.5}
                        readOnly
                        size="small"
                        sx={{
                          fontSize: { xs: "1rem", sm: "1.05rem" },
                        }}
                      />
  
                      <Typography
                        sx={{
                          color: style.color4,
                          fontSize: "0.79rem",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rating} ( {ratingCount} rating)
                      </Typography>
                    </>
                  ):
                ratingCount > 1 ? (
                  <>
                    <Rating
                      value={rating}
                      precision={0.5}
                      readOnly
                      size="small"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.05rem" },
                      }}
                    />

                    <Typography
                      sx={{
                        color: style.color4,
                        fontSize: "0.79rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rating} ( {ratingCount} ratings)
                    </Typography>
                  </>
                ) : (
                  <Typography
                    sx={{
                      color: style.color3,
                      fontSize: "0.79rem",
                    }}
                  >
                    No reviews yet
                  </Typography>
                )}
              </Box>

              <Typography
                sx={{
                  mt: 0.65,
                  color: style.color3,
                  fontSize: { xs: "0.82rem", sm: "0.86rem" },
                  lineHeight: 1.4,
                  minHeight: { xs: "2.8em", sm: "2.8em" },
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                }}
              >
                {productData.cardSummary || productData.shortDescription}
              </Typography>

              <Box sx={{ mt: "auto", pt: 0.75 }}>
                {isFree ? (
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography
                      sx={{
                        color: "#20d45b",
                        fontSize: { xs: "1.35rem", sm: "1.5rem" },
                        lineHeight: 1,
                        fontWeight: 900,
                      }}
                    >
                      FREE
                    </Typography>

                    <Typography
                      sx={{
                        color: style.color3,
                        fontSize: "0.76rem",
                      }}
                    >
                      Instant download
                    </Typography>
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    spacing={0.7}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {hasPromoDiscount && savingsPercent > 0 && (
                      <Chip
                        label={`${savingsPercent}% OFF`}
                        size="small"
                        sx={{
                          height: 22,
                          borderRadius: "5px",
                          color: "#111",
                          backgroundColor: "#ffcc4d",
                          border: "1px solid rgba(255,255,255,0.34)",
                          fontWeight: 900,
                          fontSize: "0.67rem",
                          letterSpacing: "0.02em",
                          "& .MuiChip-label": {
                            px: 0.9,
                          },
                        }}
                      />
                    )}

                    {comparisonPrice && comparisonPrice > finalPrice && (
                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "0.86rem",
                          lineHeight: 1,
                          textDecoration: "line-through",
                        }}
                      >
                        ${formatPrice(comparisonPrice)}
                      </Typography>
                    )}

                    <Typography
                      sx={{
                        color: style.color4,
                        fontWeight: 900,
                        fontSize: { xs: "1.25rem", sm: "1.38rem" },
                        lineHeight: 1,
                      }}
                    >
                      ${formatPrice(finalPrice)}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>
          </Stack>

          <Box sx={{ mt: "auto", pt: 1.05 }}>
            {isFree ? (
              <>
                <Stack direction="row" spacing={0.8}>
                  <Button
                    type="button"
                    onClick={openDownloadMenu}
                    disabled={!hasDownloadOption}
                    endIcon={<KeyboardArrowDownRoundedIcon />}
                    sx={{
                      flex: 1.35,
                      minWidth: 0,
                      minHeight: 42,
                      borderRadius: "7px",
                      textTransform: "none",
                      fontWeight: 900,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.06)",
                      },
                    }}
                  >
                    Download Free
                  </Button>

                  <Button
                    component={RouterLink}
                    to={productUrl}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      flex: 0.85,
                      minWidth: 0,
                      minHeight: 42,
                      borderRadius: "7px",
                      textTransform: "none",
                      fontWeight: 800,
                      color: style.color4,
                      border: "1px solid rgba(255,255,255,0.12)",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                        borderColor: "rgba(255,255,255,0.20)",
                      },
                    }}
                  >
                    Details
                  </Button>
                </Stack>

                <Menu
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor)}
                  onClose={closeDownloadMenu}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  MenuListProps={{
                    "aria-label": `Download ${productData.name}`,
                  }}
                  PaperProps={{
                    sx: {
                      mb: 0.75,
                      minWidth: 210,
                      color: style.color4,
                      backgroundColor: "#0b111a",
                      backgroundImage: "none",
                      border: "1px solid rgba(255,255,255,0.14)",
                      boxShadow: "0 14px 32px rgba(0,0,0,0.65)",

                      "& .MuiMenuItem-root": {
                        minHeight: 44,
                        fontWeight: 700,
                      },

                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "rgba(255,255,255,0.09)",
                      },
                    },
                  }}
                >
                  {hasWindowsDownload && (
                    <MenuItem
                      onClick={() =>
                        registerAndDownloadFreeProduct("windows")
                      }
                    >
                      <WindowsRoundedIcon sx={{ mr: 1.1, fontSize: 20 }} />
                      Windows
                    </MenuItem>
                  )}

                  {hasMacDownload && (
                    <MenuItem
                      onClick={() =>
                        registerAndDownloadFreeProduct("mac")
                      }
                    >
                      <AppleIcon sx={{ mr: 1.1, fontSize: 20 }} />
                      macOS
                    </MenuItem>
                  )}
                </Menu>
              </>
            ) : (
              <>
                <Stack direction="row" spacing={0.8}>
                  <Button
                    type="button"
                    onClick={handleAddToCart}
                    startIcon={<AddShoppingCartRoundedIcon />}
                    sx={{
                      flex: 1.35,
                      minWidth: 0,
                      minHeight: 42,
                      borderRadius: "7px",
                      textTransform: "none",
                      fontWeight: 900,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.06)",
                      },
                    }}
                  >
                    Add to Cart
                  </Button>

                  <Button
                    component={RouterLink}
                    to={productUrl}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      flex: 0.85,
                      minWidth: 0,
                      minHeight: 42,
                      borderRadius: "7px",
                      textTransform: "none",
                      fontWeight: 800,
                      color: style.color4,
                      border: "1px solid rgba(255,255,255,0.12)",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                        borderColor: "rgba(255,255,255,0.20)",
                      },
                    }}
                  >
                    Details
                  </Button>
                </Stack>

                <Typography
                  component={RouterLink}
                  to="/subscribe"
                  sx={{
                    mt: 0.7,
                    display: "block",
                    color: style.color3,
                    textDecoration: "none",
                    textAlign: "center",
                    fontSize: "0.74rem",
                    lineHeight: 1.25,
                    "&:hover": {
                      color: style.color4,
                      textDecoration: "underline",
                    },
                  }}
                >
                  Included in Full Access Pass →
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      <LoginRegisterModal
        isOpen={isLoginRegisterModalOpen}
        close={closeLoginRegisterModal}
        loginComplete={loginCallback}
      />
    </>
  );
}
